import { h, render, type VNode } from "vue";
import { useI18n } from "vue-i18n";

import LeAboutDictionaryButton from "@/components/articles/LeAboutDictionaryButton.vue";
import LeArticleFeedbackButton from "@/components/articles/LeArticleFeedbackButton.vue";
import LeCompactFooter from "@/components/articles/LeCompactFooter.vue";
import LeDictionaryCover from "@/components/dictionaries/LeDictionaryCover.vue";
import type { Dictionary, DictionaryMethod } from "@/types";

export function useArticleInject(baseClass: string) {
  const { t } = useI18n();

  const inject = (
    element: Element,
    placement: "afterbegin" | "afterend" | "beforeend",
    type: string,
    injection: VNode
  ): void => {
    const placeholder = document.createElement("div");
    placeholder.classList.add(`${baseClass}__${type}`);

    element.insertAdjacentElement(placement, placeholder);
    render(injection, placeholder);
  };

  const injectDictionaryCover = (header: Element, dictionary: Dictionary) => {
    inject(
      header,
      "afterbegin",
      "cover",
      h(LeDictionaryCover, { dictionary, size: "large", withTooltip: true, withSubtitle: true })
    );
  };

  const injectFeedbackButton = (header: Element, onClick: () => void) => {
    inject(
      header,
      "beforeend",
      "feedback",
      h(LeArticleFeedbackButton, {
        tooltip: t("le-dictionary-feedback.button-tooltip.text"),
        onClick
      })
    );
  };

  const injectDictionaryInfoButton = (header: Element, dictionary: Dictionary) => {
    inject(
      header,
      "beforeend",
      "information",
      h(LeAboutDictionaryButton, { dictionary, tooltip: t("le-dictionary-info-button.tooltip") })
    );
  };

  const injectExpandToggle = (body: Element, iconButton: VNode) => {
    inject(body, "afterbegin", "expand-toggle", iconButton);
  };

  const injectCompactFooter = (footer: Element, method: DictionaryMethod, dictionary: Dictionary) => {
    inject(footer, "afterend", "footer", h(LeCompactFooter, { method, dictionary }));
  };

  return {
    // Header elements
    injectDictionaryCover,
    injectFeedbackButton,
    injectDictionaryInfoButton,

    // Body elements
    injectExpandToggle,

    // Footer elements
    injectCompactFooter
  };
}
